import {
  AnyWidgetAttributes,
  APIChartWidgetAttributes,
  APIDashboardAttributes,
  APIDashboardFilterExpression,
  APIFlatDashboardFilterSuggestion,
  APIHierarchicalDashboardFilterSuggestion,
  APIFieldAttributes,
  APIFieldType,
  APIInitialDashboardFilterParameter,
  APILinkedDashboardFilterParameter,
  APIReportFilters,
  ArdoqId,
  BubbleChartBackgroundMode,
  BubbleChartCustomQuadrantSettings,
  DashboardFilterValues,
  DrilldownScopeData,
  HexColor,
  HTTPStatusCode,
  NoteWidgetAttributes,
  Operator,
  SupportedDashboardFilterReferenceType,
  SupportedDashboardFilterType,
  WidgetDatasource,
  WidgetTypes,
} from '@ardoq/api-types';
import {
  DashboardBuilderWidget,
  DashboardBuilderWidgetsProps,
  NonEmptyWidget,
  WidgetLinkedDashboardFilter,
} from './widgets/types';
import { Layout } from 'react-grid-layout';
import { Locale } from '@ardoq/locale';
import {
  CopyEntityURLButtonProps,
  NewFeatureNotificationProps,
} from '@ardoq/snowflakes';
import {
  GetComponentLinkHandler,
  HasComponentReadPermission,
} from '@ardoq/report-reader';
import { Maybe } from '@ardoq/common-helpers';
import { DashboardFilterReaderIslandRenderProps } from './filter/DashboardFilterIsland';
import { EnhancedScopeData } from '@ardoq/data-model';
import type { I18nLocale } from '@ardoq/i18n';
export type AddWidgetButtonsProps = {
  createNewChartWidget: VoidFunction;
  createNewHeaderWidget: VoidFunction;
  createNewNoteWidget: VoidFunction;
  createNewPowerBiIframeWidget: VoidFunction;
  canCreateNewChart?: boolean;
  showPowerBiIframeWidget?: boolean;
};

export enum DashboardModes {
  EDIT = 1,
  VIEW,
  METRICS,
}

export type TrackEvent = (
  message: string,
  metadata?: Record<
    string,
    string | number | boolean | null | Record<string, string[]>
  >
) => void;

export type BubbleChartViewSettingsOverrides = {
  selectedFieldNameX: string;
  selectedFieldNameY: string;
  selectedFieldNameRadius: string;
  background: BubbleChartBackgroundMode | undefined;
  customQuadrantSettings: BubbleChartCustomQuadrantSettings | undefined;
  showBubblesWithZeroValue: boolean | undefined;
  selectedComponentTypes: string[] | undefined;
};

type WidgetDrilldownArgs = {
  datasource: WidgetDatasource;
  filters?: APIReportFilters;
  name?: string;
  widgetType?: WidgetTypes;
  dashboardId?: ArdoqId;
};

export type OnWidgetDrilldown = (args: WidgetDrilldownArgs) => void;

export type ReportFromSelectedWidgetId = (
  reportId: ArdoqId | null
) => string | null;

export type OpenFullscreenWidget = (
  widgetData: FullscreenWidgetData,
  globalFilterValues?: DashboardFilterValues
) => void;

export type FullscreenWidgetData =
  | APIChartWidgetAttributes
  | NoteWidgetAttributes;

export type ContextDashboardIsUsedIn =
  | 'reader'
  | 'discover'
  | 'usage-metrics'
  | 'presentation'
  | 'home-page'
  | 'home-page-settings'
  | 'application-completeness';

export interface DashboardProps {
  dashboardData: (APIDashboardAttributes & { _time: Date }) | null;
  isLoading: boolean;
  hideSpinner?: boolean;
  onWidgetDrilldown?: OnWidgetDrilldown;
  userLocale: Locale;
  trackEvent: TrackEvent;
  apiError?: DashboardApiError;
  dashboardMode?: DashboardModes;
  getReportNameById?: ReportFromSelectedWidgetId;
  openFullscreenWidget: OpenFullscreenWidget;
  fullscreenWidgetProps?: FullscreenWidgetProps;
  contextDashboardIsUsedIn: ContextDashboardIsUsedIn;
  newFeatureNotifications?: NewFeatureNotificationProps[];
  getComponentLinkHandler?: GetComponentLinkHandler;
  getHasComponentReadPermission?: (
    scopeData: EnhancedScopeData
  ) => HasComponentReadPermission;
  clearAllSelectedFilterValues?: VoidFunction;
  copyUrlButtonProps?: CopyEntityURLButtonProps;
  dashboardColors: Record<string, HexColor>;
  dashboardFilterIslandProps?: DashboardFilterReaderIslandRenderProps;
  downloadButtonProps?: { label: string; onClick: VoidFunction };
  isCaptureMode?: boolean;
}

type FullscreenNoteWidgetProps =
  | {
      isNoteWidget: true;
      widgetData: NoteWidgetAttributes;
    }
  | {
      isNoteWidget: false;
    };

export type FullscreenChartWidgetContentProps = {
  widgetData: NonEmptyWidget<APIChartWidgetAttributes>;
  chartData: NonEmptyWidget<APIChartWidgetAttributes>;
  // whether the widget's datasource is an inline viewpoint
  hasViewpointDatasource: boolean;
  openWidgetDataSource: VoidFunction;
  onWidgetDrilldown: OnWidgetDrilldown;
  activeLinkedDashboardFilters: WidgetLinkedDashboardFilter[];
  colors: Record<string, HexColor>;
  getComponentLinkHandler?: GetComponentLinkHandler;
  getHasComponentReadPermission?: (
    scopeData: DrilldownScopeData
  ) => HasComponentReadPermission;
  dashboardId?: ArdoqId;
  userLocale: Locale;
  dashboardMode: DashboardModes;
};

type FullscreenChartWidgetProps =
  | ({
      isNonEmptyChartWidget: true;
    } & FullscreenChartWidgetContentProps)
  | { isNonEmptyChartWidget: false };

export type FullscreenWidgetProps =
  | { isOpen: false; onClose: VoidFunction; widgetData: null }
  | ({
      onClose: VoidFunction;
      isOpen: boolean;
      widgetData: FullscreenWidgetData;
      userLocale: Locale;
      dashboardMode: DashboardModes;
      dashboardId?: ArdoqId;
      widgetName?: string;
      i18nLocale: I18nLocale;
    } & FullscreenNoteWidgetProps &
      FullscreenChartWidgetProps);

export type LoadedDashboard = Omit<
  APIDashboardAttributes,
  'widgets' | 'settings'
> &
  Pick<EditModeDashboardDataProps, 'settings'> & {
    widgets: Array<AnyWidgetAttributes>;
    _time: Date;
  };

// What we have in state
export type EditModeDashboardDataState = {
  _id?: ArdoqId;
  _version?: number;
  name: string;
  description?: string;
  widgets: DashboardBuilderWidget[];
  settings: { colors: Array<DashboardColor> };
  validationErrors?: Set<DashboardErrorType>;
  paletteColors?: HexColor[] | null;
  periodStart?: Date;
  periodEnd?: Date;
  globalFilterParameters: DashboardFilterParameter[];
  globalFilterValues?: DashboardFilterValues;
  scenarioBranchIds: Maybe<ArdoqId[]>;
};

// What we have in view model
export type EditModeDashboardDataProps = Omit<
  EditModeDashboardDataState,
  'widgets'
> & {
  widgets: DashboardBuilderWidgetsProps[];
};

export type DashboardFiltersCommands = {
  changeLinkModeDashboardFilterParameter: (
    dashboardFilterId: ArdoqId | null, // TODO: Make command specifically for exit and improve type
    type: SupportedDashboardFilterType | undefined
  ) => void;
  createDashboardFilterParameter: (name: string, id: string) => void;
  removeDashboardFilterParameter: (
    dashboardFilterId: ArdoqId,
    type: SupportedDashboardFilterType | undefined
  ) => void;
  updateDashboardFilterParameterName: (
    dashboardFilterId: ArdoqId,
    newName: string
  ) => void;
  reorderDashboardFilterParameters: (
    fromIndex: number,
    toIndex: number
  ) => void;
  updateDashboardFilterValues: (
    dashboardFilterId: string,
    type: SupportedDashboardFilterType,
    values: APIDashboardFilterExpression[] | null
  ) => void;
  editWidgetLinkModeDashboardFilterBinding: (widgetId: ArdoqId) => void;
  unlinkWidgetWithDashboardFilter: (
    widgetId: ArdoqId,
    dashboardFilterParameterId: ArdoqId
  ) => void;
  linkWidgetWithDashboardFilter: (
    widgetId: ArdoqId,
    dashboardFilterParameterId: ArdoqId
  ) => void;
  clearAllSelectedValues: () => void;
  requestDashboardFilterSuggestions: (
    parameterId: ArdoqId,
    query: string
  ) => void;
  requestDashboardFilterHierarchicalSuggestions: (
    parameterId: ArdoqId,
    query: string
  ) => void;
  filterCachedHierarchicalSuggestions: (
    parameterId: ArdoqId,
    query: string
  ) => void;
  setShowLinkChartPopover: (show: boolean) => void;
};

export type WidgetsCommands = {
  createNewWidget: (layout: Omit<Layout, 'i'>, viewType?: WidgetTypes) => void;
  copyWidget: (id: ArdoqId) => void;
  changeLayout: (newLayout: Layout[]) => void;
  deleteWidget: (id: ArdoqId, widgetType?: WidgetTypes) => void;
  selectWidget: (id: ArdoqId) => void;
};

export type FlatDashboardFilterSuggestion = APIFlatDashboardFilterSuggestion;
export type HierarchicalDashboardFilterSuggestion =
  APIHierarchicalDashboardFilterSuggestion & {
    children: HierarchicalDashboardFilterSuggestion[];
  };

type DashboardFilterSuggestionsBase = {
  parameterId: ArdoqId;
  query: string | null;
  searchedDashboardFilterValues?: DashboardFilterValues;
  isLoading?: boolean;
  widgetFilteredReports?: string[];
};

export type FlatDashboardFilterSuggestions = DashboardFilterSuggestionsBase & {
  kind: 'flat';
  suggestions: FlatDashboardFilterSuggestion[];
};

export type HierarchicalDashboardFilterSuggestions =
  DashboardFilterSuggestionsBase & {
    kind: 'hierarchical';
    suggestions: HierarchicalDashboardFilterSuggestion[];
  };

export type DashboardFilterSuggestions =
  | FlatDashboardFilterSuggestions
  | HierarchicalDashboardFilterSuggestions;
export type DashboardFiltersSuggestionsById = Record<
  ArdoqId, // dashboard filter parameter id
  DashboardFilterSuggestions
>;

export type DashboardFilterWithHierarchicalSuggestions = DashboardFilterBase & {
  type: SupportedDashboardFilterReferenceType;
  suggestionProps?: HierarchicalDashboardFilterSuggestions;
  values: APIDashboardFilterExpression[];
};

export const MAINLINE = 'mainline' as const;
export type SelectedBranch = typeof MAINLINE | ArdoqId;

export type DashboardColor = {
  dataPoint: string;
  color: HexColor;
  widgetsUsingThis: ArdoqId[]; // use this to warn if the user is changing a color that is used in multiple widgets
  isManuallyAssigned: boolean;
};

export type RememberedColorsByDashboardId = Record<
  ArdoqId,
  Record<string, HexColor>
>;

export enum DashboardErrorType {
  MISSING_NAME,
}

export type DashboardApiError = {
  message: string;
  traceId: string;
  code: HTTPStatusCode;
};

export type DashboardFiltersLinkCountDict = Record<
  string,
  { total: number; warnings: number; widgetIds: ArdoqId[] }
>;

export type DashboardFilterGlobalFieldsDict = {
  [fieldName: string]:
    | {
        name: string;
        label: string;
        type: APIFieldType;
      }
    | APIFieldAttributes;
};

export type InitialDashboardFilterParameter =
  APIInitialDashboardFilterParameter & {
    fieldLabel: undefined;
  };

export type LinkedDashboardFilterParameter =
  APILinkedDashboardFilterParameter & {
    fieldLabel: string;
  };

export type DashboardFilterParameter =
  | InitialDashboardFilterParameter
  | LinkedDashboardFilterParameter;

type DashboardFilterBase = Omit<
  InitialDashboardFilterParameter,
  'type' | 'fieldLabel' | 'fieldApiName'
> & {
  field: {
    name: string; // field key
  };
  linkCounts: {
    warnings: number;
    total: number;
  };
};

export type NumberDashboardFilter = DashboardFilterBase & {
  type: APIFieldType.NUMBER;
  numberFormatOptions: string | null;
  value: number | null;
  operator: Operator;
};

export type DateDashboardFilter = DashboardFilterBase & {
  type: APIFieldType.DATE_TIME | APIFieldType.DATE_ONLY;
  value: string | null;
  operator: Operator;
};

export type CheckboxDashboardFilter = DashboardFilterBase & {
  type: APIFieldType.CHECKBOX;
  options: [
    { value: true; label: string },
    { value: false; label: string },
    {
      value: null;
      label: 'Not set or N/A';
    },
  ];
  values: APIDashboardFilterExpression[];
};

export type ListDashboardFilter = DashboardFilterBase & {
  type: APIFieldType.LIST | APIFieldType.SELECT_MULTIPLE_LIST;
  options: [
    {
      value: null;
      label: 'Not set or N/A';
    },
    ...Array<{ value: string; label: string }>,
  ];
  values: APIDashboardFilterExpression[];
};

export type DashboardFilterWithSuggestions = DashboardFilterBase & {
  type:
    | APIFieldType.USER
    | APIFieldType.TEXT
    | SupportedDashboardFilterReferenceType;
  suggestionProps?: DashboardFilterSuggestions;
  values: APIDashboardFilterExpression[];
};

export type UnlinkedDashboardFilter = {
  id: string;
  name: string;
  field: { name: undefined };
  type: undefined;
  linkCounts: {
    warnings: 0;
    total: 0;
  };
};

export type DashboardFilter =
  | DateDashboardFilter
  | NumberDashboardFilter
  | CheckboxDashboardFilter
  | ListDashboardFilter
  | DashboardFilterWithSuggestions;

export type MinimalFieldRepresentation = {
  name: string;
  label: string;
  type: APIFieldType;
};
